import { useRef, useState, type ReactNode } from 'react';
import { Popover } from 'radix-ui';
import { BookOpen } from 'lucide-react';
import { glossary, type GlossaryKey } from '@/lib/glossary';
import { cx } from '@/lib/cx';

/**
 * A word of the trade with its explanation one hover (or tap) away. A popover rather than a tooltip so it also opens
 * on touch screens and with the keyboard.
 */
export function Term({ k, children, className }: { k: GlossaryKey; children?: ReactNode; className?: string }) {
  const entry = glossary(k);
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const hover = (next: boolean) => {
    clearTimeout(closeTimer.current);
    if (next) setOpen(true);
    else closeTimer.current = setTimeout(() => setOpen(false), 150);
  };

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          onPointerEnter={(e) => e.pointerType === 'mouse' && hover(true)}
          onPointerLeave={(e) => e.pointerType === 'mouse' && hover(false)}
          onClick={(e) => {
            // A tap toggles; a mouse already opened it by hovering, so the click keeps it open.
            e.preventDefault();
            e.stopPropagation();
            clearTimeout(closeTimer.current);
            setOpen((o) => (e.nativeEvent instanceof PointerEvent && e.nativeEvent.pointerType === 'mouse' ? true : !o));
          }}
          className={cx(
            'inline cursor-help rounded-sm [font:inherit] [letter-spacing:inherit] [text-transform:inherit] underline decoration-gold-500/70 decoration-dotted decoration-[1.5px] underline-offset-[3px] transition hover:decoration-gold-600 focus-visible:shadow-focus focus-visible:outline-none',
            className,
          )}
        >
          {children ?? entry.word}
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side="top"
          sideOffset={6}
          collisionPadding={12}
          onMouseEnter={() => hover(true)}
          onMouseLeave={() => hover(false)}
          onOpenAutoFocus={(e) => e.preventDefault()}
          className="z-[75] w-72 rounded-xl border border-gold-200 bg-surface-0 p-3.5 text-left shadow-e3 data-[state=open]:animate-rise"
        >
          <TermBody k={k} />
          <Popover.Arrow className="fill-surface-0" />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

/** The word, what it means and an example: used by the bubble and by the help panel. */
export function TermBody({ k }: { k: GlossaryKey }) {
  const entry = glossary(k);
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[13.5px] font-semibold text-ink-900">
        <BookOpen className="size-3.5 text-gold-600" aria-hidden />
        {entry.word}
      </div>
      <p className="mt-1 text-[13px] leading-snug font-normal text-ink-700">{entry.text}</p>
      {entry.example && <p className="mt-1.5 text-[12.5px] leading-snug font-normal text-ink-500 italic">Ejemplo: {entry.example}</p>}
    </div>
  );
}
